// Conquer the Flag — rules engine. applyAction clones the state, applies one legal action, then
// settle() works out who decides next.
//
// The board is a ring of 7-hex cells; territory is whoever owns each cell. Every unit moves by facing
// and has a guard zone. An activation is movement and then at most one action (tag a unit in your
// guard zone, pick up the flag, claim your cell, or use the outpost you stand on); the action ends the
// activation. Tagged units drop the flag where they stand and respawn in their teleporter cell. Carrying
// the enemy flag onto your own HQ hex wins the game (Flag Conquest). Outposts are dealt at the start;
// active effects queue a Trigger, and settle() makes the first queued trigger the pending decision.

import { Hex, hexEq, distance, neighbors, ahead, mirror, DIRECTION_NAMES } from './board';
import { buildCells, homeCell, hqOf, mapIndex, onMap, cellIdAt, deployment } from './map';
import type {
  CtfConfig, CtfState, CtfAction, CtfPlayer, PlayerInit, Team, Unit, EndReason, EffectId, Trigger, ArmyState, AbilityId,
} from './types';
import { zoneAt, typeOf, hasTrait, unitIds, slug, unitLabel } from './units';

// ── Seeded RNG (mulberry32) ──────────────────────────────────────────────────

function rngStep(state: number): [number, number] {
  state = (state + 0x6D2B79F5) | 0;
  let t = Math.imul(state ^ (state >>> 15), 1 | state);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return [state, ((t ^ (t >>> 14)) >>> 0) / 4294967296];
}

// A whole number in [min, max] and the next RNG state.
export function nextRng(rng: number, min: number, max: number): [number, number] {
  const [nextState, r] = rngStep(rng);
  return [Math.floor(r * (max - min + 1)) + min, nextState];
}

// ── Dice ─────────────────────────────────────────────────────────────────────

// Tags roll one d8: 5+ before modifiers (50%, as 4+ on the d6 before 6.0), always 2+ to 8+.
// Each +1/−1 moves the odds by 12.5%.
export const DIE = 8;
export const BASE_NEED = 5;

// ── Helpers ──────────────────────────────────────────────────────────────────

function roll(s: CtfState, min: number, max: number): number {
  const [v, next] = nextRng(s.rng, min, max);
  s.rng = next;
  return v;
}

// ── Army abilities ───────────────────────────────────────────────────────────

export const REROLLS = 3;              // Rerolls: per game
export const PASS_RANGE = 3;           // Pass: to a teammate this many hexes away or closer
export const SPRINT_MOVE = 2;          // Sprint: extra Move

// A team's army ability (none in configs from before abilities existed).
export const abilityOf = (c: CtfConfig, t: Team): AbilityId | null => c.abilities?.[t] ?? null;
const freshArmy = (c: CtfConfig, t: Team): ArmyState => ({ rerolls: abilityOf(c, t) === 'reroll' ? REROLLS : 0, passUsed: false, sprintUsed: false, rallyUsed: false });
// A team's army abilities as they stand (games saved before abilities existed have none used).
export function armyOf(s: CtfState, t: Team): ArmyState {
  return s.army?.[t] ?? freshArmy(s.config, t);
}
function army(s: CtfState, t: Team): ArmyState {
  if (!s.army) s.army = { A: freshArmy(s.config, 'A'), B: freshArmy(s.config, 'B') };
  return s.army[t];
}

export const TEAM_NAMES: Record<Team, string> = { A: 'Blue', B: 'Red' };
const N = TEAM_NAMES;
const TEAMS: Team[] = ['A', 'B'];

export const other = (t: Team): Team => (t === 'A' ? 'B' : 'A');
export const teamOf = (s: CtfState, playerId: string): Team | null => s.players.find(p => p.id === playerId)?.team ?? null;
export const playerOf = (s: CtfState, t: Team): CtfPlayer => s.players.find(p => p.team === t)!;

export function unitById(s: CtfState, id: string): Unit {
  const u = s.units.find(x => x.id === id);
  if (!u) throw new Error(`No unit ${id}`);
  return u;
}
export function unitAt(s: CtfState, h: Hex): Unit | null {
  // Prisoners standing in their prison (6.2) take up their hex too.
  return s.units.find(u => hexEq(u.pos, h)) ?? null;
}

// Who owns the ground at a hex (null: neutral, or off the map).
export function territory(s: CtfState, h: Hex): Team | null {
  const id = cellIdAt(s.cells, h);
  return id === undefined ? null : s.owners[id];
}

export function inOwnHqCell(s: CtfState, u: Unit, at: Hex | null = u.pos): boolean {
  return !!at && cellIdAt(s.cells, at) === homeCell(s.cells, u.team, 'hq').id;
}

// A unit as players see it on the board: "Blue 2", "Red L".
const nm = (s: CtfState, u: Unit) => `${N[u.team]} ${unitLabel(s, u)}`;

function log(s: CtfState, kind: string, team: Team | null, text: string, data?: Record<string, string | number | boolean | null>) {
  s.log.push({ round: s.round, kind, team, text, ...(data ? { data } : {}) });
}

function clone(state: CtfState): CtfState {
  const { log: entries, cells, config, ...rest } = state;
  const s = copyJson(rest) as CtfState;
  s.cells = cells;                      // fixed for the game: shared, never mutated
  s.config = config;                    // likewise
  s.log = entries.slice();
  return s;
}

// A deep copy of plain JSON data, as JSON.parse(JSON.stringify(x)) would make it (undefined fields
// dropped) but without the round trip through a string.
function copyJson(x: unknown): unknown {
  if (x === null || typeof x !== 'object') return x;
  if (Array.isArray(x)) {
    const out = new Array(x.length);
    for (let i = 0; i < x.length; i++) out[i] = x[i] === undefined ? null : copyJson(x[i]);
    return out;
  }
  const out: Record<string, unknown> = {};
  for (const k in x as Record<string, unknown>) {
    const v = (x as Record<string, unknown>)[k];
    if (v !== undefined) out[k] = copyJson(v);
  }
  return out;
}

// A unit can tag an enemy unit inside its guard zone (`from`/`facing`: where the tagger would stand).
export function canTag(s: CtfState, tagger: Unit, target: Unit, from: Hex | null = tagger.pos, facing = tagger.facing): boolean {
  if (target.team === tagger.team || target.inPrison || !target.pos || !from) return false;
  return zoneHexes(s, { ...tagger, pos: from, facing }).some(z => hexEq(z, target.pos));
}

// Watchtower: a bonus to tag rolls against units inside cells the tagger's team holds.
export function tagBonus(s: CtfState, taggerTeam: Team, targetAt: Hex | null): number {
  if (!targetAt || territory(s, targetAt) !== taggerTeam) return 0;
  return s.config.watchtowerBonus * effectHeld(s, taggerTeam, 'watchtower');
}

// Open hex a unit may step onto: on the map and empty.
export function canEnter(s: CtfState, u: Unit, h: Hex): boolean {
  return onMap(s.cells, h) && !unitAt(s, h);
}

// Guard zones: the unit type's zone, turned to the unit's facing. A unit tags and assists only into its
// zone (nothing blocks it); while it hasn't activated this round, an enemy that moves into its zone must
// stop there.
export function zoneHexes(s: CtfState, e: Unit): Hex[] {
  return e.pos ? zoneAt(typeOf(s, e), e.pos, e.facing) : [];
}
// Whether a unit's zone stops enemies: until it activates this round, all round while on guard or for
// a sentinel, never for a unit with an open zone.
export const zoneUp = (s: CtfState, e: Unit) =>
  !hasTrait(s, e, 'noGuardZone') && (!e.activated || !!e.guarding || hasTrait(s, e, 'sentinel'));
// The units whose up zones stop `u` at h.
export function zoneHolders(s: CtfState, u: Unit, h: Hex): string[] {
  return s.units.filter(e => e.team !== u.team && !e.inPrison && zoneUp(s, e) && e.pos && zoneHexes(s, e).some(z => hexEq(z, h))).map(e => e.id);
}
export const inFreshZone = (s: CtfState, u: Unit, h: Hex) => zoneHolders(s, u, h).length > 0;

// Own-ground defence: tags against a unit standing on its own team's ground are harder.
export const groundDefense = (s: CtfState, target: Unit, at: Hex | null) =>
  at && territory(s, at) === target.team ? s.config.ownGroundDefense : 0;

// How many cells of one effect a team holds.
export function effectHeld(s: CtfState, t: Team, effect: EffectId): number {
  return s.effects.filter((e, id) => e === effect && s.owners[id] === t).length;
}

// Unit-type rules (see TraitId).
export const weightOf = (s: CtfState, u: Unit) => (hasTrait(s, u, 'heavy') ? 2 : 1);
export const canCarry = (s: CtfState, u: Unit) => !hasTrait(s, u, 'noFlag');
export const canClaim = (s: CtfState, u: Unit) => !hasTrait(s, u, 'noClaim');
// Move a turn costs: 2 with slow turns, else 1.
export const turnCost = (s: CtfState, u: Unit) => (hasTrait(s, u, 'slowTurns') ? 2 : 1);
// Reverse: a step straight backward (keeping its facing) costs this much Move.
export const REVERSE_COST = 2;
const behind = (u: Unit) => ahead(u.pos!, (u.facing + 3) % 6);
const isBackStep = (u: Unit, to: Hex) => { const b = behind(u); return b.q === to.q && b.r === to.r; };

// Move for an activation: Swiftness adds; the flag weight (and flag-heavy) slow a carrier (never below 1).
export function moveOf(s: CtfState, u: Unit): number {
  const move = typeOf(s, u).move + s.config.swiftBonus * effectHeld(s, u.team, 'swift');
  if (u.carrying !== 'flag') return move;
  const penalty = s.config.carrierMovePenalty + (hasTrait(s, u, 'flagHeavy') ? 1 : 0) - (hasTrait(s, u, 'flagCarrier') ? 1 : 0);
  return penalty > 0 ? Math.max(1, move - penalty) : move;
}

export function cellCounts(s: CtfState): Record<Team, number> {
  return { A: s.owners.filter(o => o === 'A').length, B: s.owners.filter(o => o === 'B').length };
}

// ── Setup ────────────────────────────────────────────────────────────────────

function makeUnits(s: CtfState): Unit[] {
  const spots = deployment(s.cells);
  const units: Unit[] = [];
  for (const team of TEAMS) {
    const squad = s.config.squads[team];
    const ids = unitIds(team, squad);
    squad.forEach((t, slot) => {
      const { pos, toward, outward } = spots[team][slot];
      const facing = toward ? facingTo(pos, toward) : outward ? outwardFacing(s, team, pos) : facingFor(s, team, pos);
      units.push({ id: ids[slot], team, role: slug(t.name), slot, pos, facing, inPrison: false, activated: false, carrying: null });
    });
  }
  return units;
}

const centreCellOf = (s: CtfState) => s.cells.find(c => cellIdAt(s.cells, mirror(c.centre)) === c.id)!;

// Prisons: each team's prison is the outer-ring cell (the open cells furthest from the centre) nearest
// its own teleporter; on the standard map, cell 9 for Blue and 15 for Red.
export function prisonCell(s: CtfState, t: Team): number {
  const centre = centreCellOf(s).centre;
  const open = s.cells.filter(c => !c.home);
  const far = Math.max(...open.map(c => distance(c.centre, centre)));
  const tele = homeCell(s.cells, t, 'teleporter').centre;
  return open.filter(c => distance(c.centre, centre) === far).sort((a, b) => distance(a.centre, tele) - distance(b.centre, tele) || a.id - b.id)[0].id;
}
export const prisoners = (s: CtfState, t: Team) => s.units.filter(u => u.team === t && u.inPrison);

// One step further from the centre than facing the enemy HQ (slot 5's start: its first activation
// spends a Move turning back).
function outwardFacing(s: CtfState, team: Team, pos: Hex): number {
  const f = facingFor(s, team, pos);
  const centre = centreCellOf(s).centre;
  const [l, r] = [(f + 1) % 6, (f + 5) % 6];
  return distance(ahead(pos, l), centre) >= distance(ahead(pos, r), centre) ? l : r;
}

// Start facing the enemy HQ (the neighbour direction that gets closest to it).
const facingFor = (s: CtfState, team: Team, pos: Hex) => facingTo(pos, hqOf(s.cells, other(team)));

function facingTo(pos: Hex, target: Hex): number {
  let best = 0;
  for (let f = 0; f < 6; f++) if (distance(ahead(pos, f), target) < distance(ahead(pos, best), target)) best = f;
  return best;
}

export function createGame(config: CtfConfig, players: PlayerInit[], seed = Date.now()): CtfState {
  const ps: CtfPlayer[] = TEAMS.map((team, i) => ({
    id: players[i]?.id ?? `player-${i + 1}`, name: players[i]?.name ?? `Player ${i + 1}`, isBot: players[i]?.isBot ?? false, team,
  }));
  const cells = buildCells(config.rings);
  const s: CtfState = {
    version: 6, config, phase: 'playing', players: ps,
    round: 0, startTeam: 'A', firstTeam: 'A', turn: 'A',
    activations: { A: { allowed: 0, used: 0 }, B: { allowed: 0, used: 0 } },
    cells, owners: cells.map(c => c.home), effects: cells.map(() => null), units: [],
    flags: { A: { pos: null, carrierId: null }, B: { pos: null, carrierId: null } },
    army: { A: freshArmy(config, 'A'), B: freshArmy(config, 'B') },
    queue: [], active: null, pending: null,
    result: null, winner: null, rng: seed | 0, log: [],
  };
  startGame(s);
  settle(s);
  return s;
}

// Outposts for the game.
function dealEffects(s: CtfState) {
  s.effects = s.cells.map(() => null);
  const c = s.config;
  const pool = c.effectPool;
  const count = c.outposts === 'none' ? 0 : c.outposts === 'centre' ? 1 : c.outpostCount;
  if (count <= 0 || !pool.length) return;
  const shuffle = <T>(xs: T[]) => {
    const a = [...xs];
    for (let i = a.length - 1; i > 0; i--) { const j = roll(s, 0, i); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
  };
  const centre = centreCellOf(s).id;
  const spots = c.outposts === 'centre' ? [centre] : mirroredCells(s, count, shuffle);
  // A chosen centre effect (when the centre cell is an outpost): the others are dealt from the rest of the pool.
  const fixedCentre = c.centreEffect && spots.includes(centre) ? c.centreEffect : null;
  const rest = fixedCentre ? pool.filter(e => e !== fixedCentre) : pool;
  const deal: EffectId[] = [];
  if (fixedCentre) { spots.splice(spots.indexOf(centre), 1); spots.unshift(centre); deal.push(fixedCentre); }
  while (deal.length < spots.length) deal.push(...shuffle(rest.length ? rest : pool));
  spots.forEach((id, i) => { s.effects[id] = deal[i]; });
  log(s, 'effects', null, `Outposts: ${spots.map((id, i) => `${EFFECT_NAMES[deal[i]]} on cell ${id}`).join(', ')}.`);
}

// Random outpost cells that are fair to both teams: pairs of cells mirrored across the centre
// (the map's 180° symmetry), plus the centre cell when the count is odd.
function mirroredCells(s: CtfState, count: number, shuffle: <T>(xs: T[]) => T[]): number[] {
  const twin = (id: number) => cellIdAt(s.cells, mirror(s.cells[id].centre))!;
  const open = s.cells.filter(c => !c.home);
  const centres = open.filter(c => twin(c.id) === c.id).map(c => c.id);
  const pairs = shuffle(open.filter(c => twin(c.id) > c.id).map(c => c.id));
  const out = count % 2 ? centres.slice(0, 1) : [];
  for (const id of pairs) if (out.length + 2 <= count) out.push(id, twin(id));
  return out;
}

export const EFFECT_NAMES: Record<EffectId, string> = {
  swift: 'Swiftness', watchtower: 'Watchtower', longShot: 'Long shot', blink: 'Blink', magnet: 'Magnet',
};
export const EFFECT_TIMING: Record<EffectId, 'static' | 'active'> = {
  swift: 'static', watchtower: 'static', longShot: 'active', blink: 'active', magnet: 'static',
};

// The start: outposts dealt, non-home cells neutral, units at their spots, the first team drawn at random.
function startGame(s: CtfState) {
  s.round = 0;
  dealEffects(s);
  s.owners = s.cells.map(c => c.home);
  s.units = makeUnits(s);
  s.flags = { A: { pos: hqOf(s.cells, 'A'), carrierId: null }, B: { pos: hqOf(s.cells, 'B'), carrierId: null } };
  s.queue = [];
  s.active = null;
  s.startTeam = roll(s, 0, 1) === 0 ? 'A' : 'B';
  log(s, 'game-start', null, `The game begins. ${N[s.startTeam]} goes first.`, { first: s.startTeam });
  startRound(s);
}

// Ends the round in progress (domination, the mercy rule) and starts the next one.
function startRound(s: CtfState) {
  if (s.round > 0 && !endOfRound(s)) return;
  s.round += 1;
  s.firstTeam = s.round === 1 ? s.startTeam : other(s.firstTeam);
  for (const u of s.units) {
    if (u.inPrison && u.respawnRound !== undefined && u.respawnRound <= s.round) respawn(s, u);
  }
  for (const u of s.units) { u.activated = false; delete u.guarding; }
  for (const t of TEAMS) { const ar = army(s, t); ar.passUsed = ar.sprintUsed = ar.rallyUsed = false; }
  // Each team activates each of its free units once (units in prison sit the round out).
  const free = (t: Team) => s.units.filter(u => u.team === t && !u.inPrison).length;
  s.activations = { A: { allowed: free('A'), used: 0 }, B: { allowed: free('B'), used: 0 } };
  const allowed = free('A');
  s.turn = s.firstTeam;
  const c = cellCounts(s);
  const acts = free('A') === free('B') ? `${allowed} activations each` : `activations Blue ${free('A')}, Red ${free('B')}`;
  log(s, 'round-start', null, `Round ${s.round}: ${acts}, ${N[s.firstTeam]} first. Cells Blue ${c.A} – Red ${c.B}.`,
    { activations: allowed, activationsB: free('B'), first: s.firstTeam, cellsA: c.A, cellsB: c.B,
      keepersA: s.units.filter(u => u.team === 'A' && inOwnHqCell(s, u)).length, keepersB: s.units.filter(u => u.team === 'B' && inOwnHqCell(s, u)).length });
  if (s.round > 1) pullFlags(s);
}

// Magnet: the team holding a Magnet outpost pulls the enemy flag, when it lies on the ground, 1 hex
// toward its own HQ: onto the neighbouring hex that brings it closest (by straight-line distance),
// never onto the HQ hex itself (a carrier still has to score). Ties go the same way for both teams
// (directions are tried in mirrored order).
function pullFlags(s: CtfState) {
  s.effects.forEach((fx, id) => {
    const t = s.owners[id];
    if (fx !== 'magnet' || !t) return;
    const flag = s.flags[other(t)];
    if (!flag.pos || flag.carrierId) return;
    const hq = hqOf(s.cells, t);
    const d0 = distance(flag.pos, hq);
    if (d0 <= 1) return;
    const xy = (h: Hex) => [1.5 * h.q, Math.sqrt(3) * (h.r + h.q / 2)];
    const [hx, hy] = xy(hq);
    let best: Hex | null = null, bestD = Infinity;
    for (let k = 0; k < 6; k++) {
      const n = neighbors(flag.pos)[(k + (t === 'B' ? 3 : 0)) % 6];
      if (!onMap(s.cells, n) || distance(n, hq) >= d0) continue;
      const [x, y] = xy(n);
      const d = Math.hypot(x - hx, y - hy);
      if (d < bestD - 1e-9) { best = n; bestD = d; }
    }
    if (!best) return;
    flag.pos = best;
    log(s, 'magnet', t, `The Magnet pulls ${N[other(t)]}'s flag 1 hex toward ${N[t]}'s HQ.`, { team: t, cell: id, q: best.q, r: best.r });
  });
}

// Round-end domination and the mercy rule. Returns false when the game ended.
function endOfRound(s: CtfState): boolean {
  const c = cellCounts(s);
  const { mercyRounds, dominationCells } = s.config;
  // Domination: holding enough cells as the round ends wins the game outright (only then: holding
  // them for a while during the round isn't enough).
  const dominant = dominationCells > 0 ? TEAMS.filter(t => c[t] >= dominationCells).sort((x, y) => c[y] - c[x])[0] : undefined;
  if (dominant) {
    endGame(s, dominant, 'domination');
    return false;
  }
  if (mercyRounds > 0 && s.round >= mercyRounds) {
    // Mercy rule: the game ends; more cells wins, otherwise a draw.
    endGame(s, c.A > c.B ? 'A' : c.B > c.A ? 'B' : null, 'mercy');
    return false;
  }
  return true;
}

function endGame(s: CtfState, winner: Team | null, reason: EndReason) {
  const cells = cellCounts(s);
  s.result = { winner, reason, rounds: s.round, cells, owners: s.owners.slice(), effects: s.effects.slice(), startTeam: s.startTeam };
  const how: Record<EndReason, string> = {
    flag: 'brings the flag home', resign: `wins: ${winner ? N[other(winner)] : 'the other side'} resigns`, mercy: `holds more cells after ${s.round} rounds (mercy rule)`,
    domination: `holds ${winner ? cells[winner] : 0} cells at the end of round ${s.round} (domination)`,
    prison: `has ${winner ? N[other(winner)] : 'the other side'}'s whole army in prison`,
  };
  log(s, 'game-end', winner, winner ? `${N[winner]} ${reason === 'resign' ? how.resign : `${how[reason]} and wins`}. Cells Blue ${cells.A} – Red ${cells.B}.`
    : `Mercy rule after ${s.round} rounds: a draw (cells Blue ${cells.A} – Red ${cells.B}).`,
    { winner, reason, rounds: s.round, cellsA: cells.A, cellsB: cells.B,
      activation: s.activations.A.used + s.activations.B.used + (s.active ? 1 : 0), firstTeam: s.firstTeam });
  s.phase = 'complete';
  s.winner = winner;
  s.pending = null;
  s.active = null;
  s.queue = [];
}

// ── Settle ───────────────────────────────────────────────────────────────────

export function actorTeam(s: CtfState): Team | null {
  if (s.phase !== 'playing') return null;
  return s.pending ? s.pending.team : s.turn;
}

const eligible = (s: CtfState, t: Team) => s.units.filter(u => u.team === t && !u.inPrison && !u.activated);

function settle(s: CtfState) {
  for (let i = 0; i < 1000; i++) {
    s.pending = null;
    if (s.phase !== 'playing') return;
    if (s.queue.length) {
      s.pending = s.queue[0];
      return;
    }
    if (s.active) {
      if (unitById(s, s.active.unitId).inPrison) { finishActivation(s); continue; }
      return;
    }
    for (const t of TEAMS) {
      const a = s.activations[t];
      if (a.used < a.allowed && eligible(s, t).length === 0) {
        log(s, 'forfeit', t, `${N[t]} has no unit left to activate and forfeits ${a.allowed - a.used} activation(s).`);
        a.used = a.allowed;
      }
    }
    const canGo = (t: Team) => s.activations[t].used < s.activations[t].allowed;
    if (canGo(s.turn)) return;
    if (canGo(other(s.turn))) { s.turn = other(s.turn); return; }
    startRound(s);
  }
  throw new Error('settle() did not converge');
}

function finishActivation(s: CtfState) {
  const u = unitById(s, s.active!.unitId);
  u.activated = true;
  s.activations[u.team].used += 1;
  s.active = null;
  s.turn = other(u.team);
}

// Makes a hand-edited position playable (the playground's set-up mode): any activation or effect
// decision in progress is dropped, units off the board wait to respawn, flags follow their carriers,
// activations are recounted from the units already activated, and `turn` is the team to act next.
export function resumeAfterEdit(state: CtfState): CtfState {
  const s = clone(state);
  s.queue = []; s.pending = null; s.active = null;
  s.round = Math.max(1, s.round);
  for (const u of s.units) {
    if (u.pos && u.inPrison) continue;   // a prisoner standing in its prison (6.2) stays there
    if (u.pos) { u.inPrison = false; delete u.respawnRound; }
    else { u.inPrison = true; u.carrying = null; u.respawnRound = Math.min(u.respawnRound ?? s.round + 1, s.round + 1); }
  }
  for (const t of TEAMS) {
    const carrier = s.units.find(u => u.team !== t && u.carrying === 'flag');
    s.flags[t] = carrier ? { pos: null, carrierId: carrier.id } : { pos: s.flags[t].pos ?? hqOf(s.cells, t), carrierId: null };
  }
  const free = (t: Team) => s.units.filter(u => u.team === t && !u.inPrison).length;
  const used = (t: Team) => s.units.filter(u => u.team === t && !u.inPrison && u.activated).length;
  s.activations = { A: { allowed: free('A'), used: used('A') }, B: { allowed: free('B'), used: used('B') } };
  s.phase = 'playing';
  s.result = null;
  s.winner = null;
  log(s, 'edit', null, `Position set by hand: round ${s.round}, ${N[s.turn]} to act.`);
  settle(s);
  return s;
}

// ── Tagging and respawn ──────────────────────────────────────────────────────

// One reason a tag roll changes, for players to see: what it is and how much (+1 = 1 harder).
export interface TagPart { text: string; delta: number }

// The d8 roll a tag needs, with the target at `at`, and why: 5+, harder on the target's own ground,
// easier with Watchtower; 1 easier for each of the attacker's teammates whose guard zone covers the
// target, 1 harder for each of the target's teammates whose guard zone covers the attacker (by weight;
// activated units still count); traits. Clamped to 2–8.
export function tagBreakdown(s: CtfState, attacker: Unit, target: Unit, at: Hex | null = target.pos): { need: number; parts: TagPart[]; clamped: boolean } {
  const name = (u: Unit) => `${N[u.team]} ${unitLabel(s, u)}`;
  const parts: TagPart[] = [];
  const add = (delta: number, text: string) => { if (delta) parts.push({ text, delta }); };
  add(groundDefense(s, target, at), 'target on its own ground');
  add(-tagBonus(s, attacker.team, at), 'Watchtower');
  if (hasTrait(s, attacker, 'sureStrike')) add(-1, `${name(attacker)} is a sure strike`);
  if (hasTrait(s, attacker, 'clumsy')) add(1, `${name(attacker)} is clumsy`);
  if (hasTrait(s, target, 'tough')) add(1, `${name(target)} is tough`);
  if (hasTrait(s, target, 'fragile')) add(-1, `${name(target)} is fragile`);
  const covers = (x: Unit, h: Hex | null) => !!h && zoneHexes(s, x).some(z => hexEq(z, h));
  for (const x of s.units) {
    if (x.inPrison || !x.pos || x.id === attacker.id || x.id === target.id) continue;
    const heavy = weightOf(s, x) > 1 ? ' (heavy)' : '';
    if (x.team === attacker.team && covers(x, at)) add(-weightOf(s, x), `${name(x)}${heavy} covers the target`);
    else if (x.team === target.team && covers(x, attacker.pos)) add(weightOf(s, x), `${name(x)}${heavy} covers the attacker`);
  }
  const raw = parts.reduce((n, p) => n + p.delta, BASE_NEED);
  const need = Math.min(DIE, Math.max(2, raw));
  return { need, parts, clamped: need !== raw };
}

export function tagNeed(s: CtfState, attacker: Unit, target: Unit, at: Hex | null = target.pos): number {
  return tagBreakdown(s, attacker, target, at).need;
}

// A breakdown as one line: "base 5, +1 target on its own ground, −1 Blue 2 covers the target".
export function tagWhy(b: { parts: TagPart[]; clamped: boolean }): string {
  const sign = (d: number) => (d > 0 ? `+${d}` : `−${-d}`);
  return [`base ${BASE_NEED}`, ...b.parts.map(p => `${sign(p.delta)} ${p.text}`), ...(b.clamped ? ['limited to 2+ to 8+'] : [])].join(', ');
}

// Chance a tag succeeds (the bot uses this too), with the target at `at`.
export function tagChance(s: CtfState, attacker: Unit, target: Unit, at: Hex | null = target.pos): number {
  return (DIE + 1 - tagNeed(s, attacker, target, at)) / DIE;
}

function tagRoll(s: CtfState, attacker: Unit, target: Unit, source: 'action' | 'longShot') {
  const verb = source === 'longShot' ? 'takes a long shot at' : 'tags at';
  const ground = territory(s, target.pos!);
  const b = tagBreakdown(s, attacker, target);
  const need = b.need;
  const d = roll(s, 1, DIE);
  const success = d >= need;
  // Rerolls (army ability): the side the roll went against may reroll it before it counts.
  const loser = success ? target.team : attacker.team;
  const canReroll = armyOf(s, loser).rerolls > 0;
  log(s, 'tag-roll', attacker.team, `${nm(s, attacker)} ${verb} ${nm(s, target)}: rolls ${d}, needs ${need}+ (${success ? 'hit' : 'miss'}).${canReroll ? ` ${N[loser]} may reroll.` : ''}`,
    { attacker: attacker.id, target: target.id, attackerRole: attacker.role, targetRole: target.role, source,
      ground: ground === null ? 'neutral' : ground === target.team ? 'own' : 'enemy', total: d, need, success, why: tagWhy(b) });
  if (canReroll) {
    s.queue.unshift({ kind: 'reroll', team: loser, attackerId: attacker.id, targetId: target.id, source, need, roll: d, hit: success });
    return;
  }
  if (success) tagOut(s, target);
}

// A tagged unit that hasn't gone yet this round loses its activation (exhaustion), drops the flag
// where it stands, and respawns at once.
function tagOut(s: CtfState, target: Unit) {
  if (!target.activated && s.active?.unitId !== target.id) {
    target.activated = true;
    const act = s.activations[target.team];
    if (act.used < act.allowed) act.used += 1;
    log(s, 'exhaust', target.team, `${nm(s, target)} is exhausted and loses its activation this round.`, { unit: target.id });
  }
  if (target.carrying === 'flag') {
    const flag = s.flags[other(target.team)];
    flag.carrierId = null;
    flag.pos = target.pos;
    log(s, 'flag-drop', target.team, `The flag drops at ${flag.pos!.q},${flag.pos!.r}.`);
  }
  target.carrying = null;
  delete target.guarding;
  // Prisons: tagged on ground the enemy holds, the unit goes to its prison instead.
  if (s.config.prisons && target.pos && territory(s, target.pos) === other(target.team)) {
    target.pos = null;
    target.inPrison = true;
    delete target.respawnRound;
    if (s.config.prisonersOnBoard) {
      target.pos = prisonSpot(s, target.team);
      target.facing = facingFor(s, target.team, target.pos);
    }
    log(s, 'jail', target.team, `${nm(s, target)} goes to prison (cell ${prisonCell(s, target.team)}) until freed.`,
      { unit: target.id, cell: prisonCell(s, target.team) });
    // The whole army in prison: that side loses at once.
    if (!s.units.some(u => u.team === target.team && !u.inPrison)) { endGame(s, other(target.team), 'prison'); return; }
  } else respawn(s, target);
  if (s.active?.unitId === target.id) finishActivation(s);
}

// Where a team's prisoner goes (or a freed one, before 6.2): the open hex nearest its prison cell's
// centre (the centre first, then the hexes furthest back from the map centre), else the nearest open
// hex on the map.
function prisonSpot(s: CtfState, t: Team): Hex {
  const cell = s.cells[prisonCell(s, t)];
  const spots = s.cells.flatMap(c => c.hexes).filter(h => !unitAt(s, h));
  spots.sort((p, q) => distance(p, cell.centre) - distance(q, cell.centre) || fromCentre(q) - fromCentre(p) || p.q - q.q || p.r - q.r);
  return spots[0];
}

// Back on the board at the open hex nearest the team's teleporter outpost.
// Squared distance from the map centre on the drawn map (hex layout, not hex steps).
const fromCentre = (h: Hex) => 3 * (h.q * h.q + h.q * h.r + h.r * h.r);

// A tagged unit reappears on its teleporter hex; if that is taken, on the nearest free hex, the one
// furthest back (from the map centre) first, the same way for both teams.
function respawn(s: CtfState, u: Unit) {
  const tele = homeCell(s.cells, u.team, 'teleporter').centre;
  let best: Hex | null = null;
  for (const c of s.cells) for (const h of c.hexes) {
    if (unitAt(s, h)) continue;
    const d = distance(h, tele), bd = best ? distance(best, tele) : Infinity;
    if (d < bd || (d === bd && best && fromCentre(h) > fromCentre(best))) best = h;
  }
  u.pos = best!;
  u.facing = facingFor(s, u.team, u.pos);
  u.inPrison = false;
  delete u.respawnRound;
  log(s, 'respawn', u.team, `${nm(s, u)} respawns in cell ${cellIdAt(s.cells, u.pos)}.`, { unit: u.id, q: u.pos.q, r: u.pos.r });
}

function checkArrival(s: CtfState, u: Unit) {
  if (u.carrying === 'flag' && u.pos && hexEq(u.pos, hqOf(s.cells, u.team))) {
    endGame(s, u.team, 'flag');
  }
}

// ── Claiming ─────────────────────────────────────────────────────────────────

// A unit may claim the cell it stands in if the cell isn't a home cell, isn't already its team's,
// touches the team's ground, and the team outnumbers the enemy in it (claimNeeds).
export function claimableCell(s: CtfState, u: Unit): number | null {
  if (!u.pos || !canClaim(s, u)) return null;
  const id = cellIdAt(s.cells, u.pos);
  if (id === undefined || s.cells[id].home || s.owners[id] === u.team) return null;
  let touches = false;
  mapIndex(s.cells).adj[id].forEach(n => { if (s.owners[n] === u.team) touches = true; });
  if (!touches) return null;
  const inCell = unitsInCell(s, id);
  return inCell[u.team] >= claimNeeds(inCell[other(u.team)]) ? id : null;
}

// Units standing in a cell, by team (weighted: a unit with weight 2 counts twice).
export function unitsInCell(s: CtfState, id: number): Record<Team, number> {
  const out = { A: 0, B: 0 };
  for (const u of s.units) if (!u.inPrison && u.pos && cellIdAt(s.cells, u.pos) === id) out[u.team] += weightOf(s, u);
  return out;
}

// How many (weighted) units a team needs in a cell to claim it: more than the enemy has there.
export const claimNeeds = (enemiesInCell: number) => enemiesInCell + 1;

// ── Effects ──────────────────────────────────────────────────────────────────

// Where a blinked unit lands in a cell: the outpost hex, else the nearest open hex.
function blinkSpot(s: CtfState, u: Unit, cell: number): Hex | null {
  const c = s.cells[cell];
  const open = c.hexes.filter(h => canEnter(s, u, h));
  open.sort((a, b) => distance(a, c.centre) - distance(b, c.centre) || a.q - b.q || a.r - b.r);
  return open[0] ?? null;
}

// Blink: any unit on the board but the flag carrier, to any other cell its team holds.
function blinkOptions(s: CtfState, t: Team): { unitId: string; cell: number }[] {
  const out: { unitId: string; cell: number }[] = [];
  for (const u of s.units) {
    if (u.team !== t || u.inPrison || !u.pos || u.carrying === 'flag') continue;
    const here = cellIdAt(s.cells, u.pos);
    s.owners.forEach((o, id) => { if (o === t && id !== here && blinkSpot(s, u, id)) out.push({ unitId: u.id, cell: id }); });
  }
  return out;
}

// Claim a cell for a team.
function claimCell(s: CtfState, t: Team, id: number, by: string) {
  const from = s.owners[id];
  s.owners[id] = t;
  const c = cellCounts(s);
  const fx = s.effects[id];
  log(s, 'claim', t, `${nm(s, unitById(s, by))} claims cell ${id}${fx ? ` (${EFFECT_NAMES[fx]})` : ''}${from ? ` from ${N[from]}` : ''}. Cells Blue ${c.A} – Red ${c.B}.`,
    { unit: by, cell: id, from: from ?? null, cellsA: c.A, cellsB: c.B, effect: fx ?? null });
}

// Active effects: a unit on the outpost of a cell its team holds may use it as its action.
function activeTrigger(s: CtfState, u: Unit): Trigger | null {
  if (!u.pos) return null;
  const id = cellIdAt(s.cells, u.pos);
  if (id === undefined || s.owners[id] !== u.team || !hexEq(u.pos, s.cells[id].centre)) return null;
  switch (s.effects[id]) {
    case 'longShot': return s.units.some(e => e.team !== u.team && !e.inPrison && e.pos) ? { kind: 'longShot', team: u.team, unitId: u.id } : null;
    case 'blink': return blinkOptions(s, u.team).length ? { kind: 'blink', team: u.team } : null;
    default: return null;
  }
}

// ── Legal actions ────────────────────────────────────────────────────────────

export function listLegalActions(s: CtfState, playerId: string): CtfAction[] {
  const team = teamOf(s, playerId);
  if (!team || team !== actorTeam(s)) return [];
  const out: CtfAction[] = [];
  const skip: CtfAction = { type: 'skipEffect', playerId };
  switch (s.pending?.kind) {
    case 'longShot': return [
      ...s.units.filter(e => e.team !== team && !e.inPrison && e.pos).map(e => ({ type: 'longShot' as const, playerId, targetId: e.id })), skip];
    case 'blink': return [...blinkOptions(s, team).map(o => ({ type: 'blink' as const, playerId, ...o })), skip];
    case 'reroll': return [{ type: 'reroll', playerId }, { type: 'keepRoll', playerId }];
  }
  const a = s.active;
  if (!a) {
    for (const u of eligible(s, team)) out.push({ type: 'activate', playerId, unitId: u.id });
    return out;
  }
  const u = unitById(s, a.unitId);
  if (!a.moveLocked) {
    const to = ahead(u.pos!, u.facing);
    if (a.movesLeft > 0 && canEnter(s, u, to)) out.push({ type: 'move', playerId, to });
    if (hasTrait(s, u, 'reverse') && a.movesLeft >= REVERSE_COST && canEnter(s, u, behind(u))) out.push({ type: 'move', playerId, to: behind(u) });
    if (a.movesLeft >= turnCost(s, u)) {
      out.push({ type: 'turn', playerId, dir: 1 }, { type: 'turn', playerId, dir: -1 });
    }
  }
  // Army abilities before moving: Sprint, or Rally from the teleporter cell.
  const ar = armyOf(s, team);
  if (!a.moved && !a.moveLocked) {
    const ability = abilityOf(s.config, team);
    if (ability === 'sprint' && !ar.sprintUsed && !a.sprinted) out.push({ type: 'sprint', playerId });
    if (ability === 'rally' && !ar.rallyUsed && !u.carrying && cellIdAt(s.cells, u.pos!) === homeCell(s.cells, team, 'teleporter').id) {
      const here = cellIdAt(s.cells, u.pos!);
      s.owners.forEach((o, id) => { if (o === team && id !== here && blinkSpot(s, u, id)) out.push({ type: 'rally', playerId, cell: id }); });
    }
  }
  // Actions: each one ends the activation.
  for (const e of s.units) if (canTag(s, u, e)) out.push({ type: 'tag', playerId, targetId: e.id });
  if (abilityOf(s.config, team) === 'pass' && !ar.passUsed && u.carrying === 'flag') {
    for (const x of s.units) {
      if (x.team === team && x.id !== u.id && !x.inPrison && x.pos && canCarry(s, x) && distance(u.pos!, x.pos) <= PASS_RANGE) out.push({ type: 'pass', playerId, toUnitId: x.id });
    }
  }
  const flag = s.flags[other(team)];
  if (!u.carrying && canCarry(s, u) && flag.pos && distance(u.pos!, flag.pos) <= s.config.flagPickupRange) out.push({ type: 'pickUp', playerId });
  if (claimableCell(s, u) !== null) out.push({ type: 'claim', playerId });
  if (activeTrigger(s, u)) out.push({ type: 'useOutpost', playerId });
  if (s.config.prisons && prisoners(s, team).length && cellIdAt(s.cells, u.pos!) === prisonCell(s, team)) out.push({ type: 'jailbreak', playerId });
  if (u.carrying === 'flag') {
    for (const to of [u.pos!, ...neighbors(u.pos!)]) if (onMap(s.cells, to)) out.push({ type: 'dropFlag', playerId, to });
  }
  out.push({ type: 'guard', playerId });
  out.push({ type: 'endActivation', playerId });
  return out;
}

function sameValue(x: unknown, y: unknown): boolean {
  if (x && y && typeof x === 'object' && typeof y === 'object') return hexEq(x as Hex, y as Hex);
  return x === y;
}

export function isLegal(s: CtfState, action: CtfAction): boolean {
  return listLegalActions(s, action.playerId).some(l => {
    if (l.type !== action.type) return false;
    const lr = l as Record<string, unknown>, ar = action as Record<string, unknown>;
    return Object.keys(lr).every(k => sameValue(lr[k], ar[k]));
  });
}

// ── applyAction ──────────────────────────────────────────────────────────────

export function applyAction(state: CtfState, action: CtfAction): CtfState {
  // Resigning is open to either player at any time while the game is on (never offered to bots).
  if (action.type === 'resign') {
    const t = teamOf(state, action.playerId);
    if (state.phase !== 'playing' || !t) return state;
    const s = clone(state);
    endGame(s, other(t), 'resign');
    return s;
  }
  if (!isLegal(state, action)) return state;
  const s = clone(state);
  const team = teamOf(s, action.playerId)!;
  const a = s.active;
  const done = () => s.queue.shift();

  switch (action.type) {
    case 'activate': {
      const u = unitById(s, action.unitId);
      s.active = { unitId: u.id, movesLeft: moveOf(s, u), moved: false, moveLocked: false };
      log(s, 'activate', team, `${nm(s, u)} activates.`, { unit: u.id, role: u.role });
      break;
    }
    case 'move': {
      const u = unitById(s, a!.unitId);
      const back = isBackStep(u, action.to);
      u.pos = action.to;
      a!.movesLeft -= back ? REVERSE_COST : 1;
      a!.moved = true;
      log(s, 'move', team, `${nm(s, u)} moves to ${action.to.q},${action.to.r}.`, { unit: u.id, q: action.to.q, r: action.to.r });
      checkArrival(s, u);
      if (s.phase !== 'playing') break;
      const holders = hasTrait(s, u, 'slippery') ? [] : zoneHolders(s, u, u.pos);
      if (holders.length) {
        a!.moveLocked = true;
        log(s, 'zone-stop', team, `${nm(s, u)} stops in the zone of ${holders.map(id => nm(s, unitById(s, id))).join(', ')}.`, { unit: u.id, by: holders.map(id => unitById(s, id).role).join(',') });
      }
      break;
    }
    case 'turn': {
      const u = unitById(s, a!.unitId);
      u.facing = (u.facing + action.dir + 6) % 6;
      a!.movesLeft -= turnCost(s, u);
      a!.moved = true;
      log(s, 'turn', team, `${nm(s, u)} turns to face ${DIRECTION_NAMES[u.facing]}.`, { unit: u.id, facing: u.facing });
      break;
    }
    case 'tag':
      tagRoll(s, unitById(s, a!.unitId), unitById(s, action.targetId), 'action');
      if (s.active) finishActivation(s);
      break;
    case 'pickUp': {
      const u = unitById(s, a!.unitId);
      log(s, 'hands', team, `${nm(s, u)} picks up the flag.`, { unit: u.id, role: u.role, item: 'flag', success: true });
      u.carrying = 'flag';
      s.flags[other(team)] = { pos: null, carrierId: u.id };
      checkArrival(s, u);                // a flag dropped next to your own HQ hex scores at once
      if (s.active) finishActivation(s);
      break;
    }
    case 'claim': {
      const u = unitById(s, a!.unitId);
      claimCell(s, team, claimableCell(s, u)!, u.id);
      finishActivation(s);
      break;
    }
    case 'useOutpost': {
      const u = unitById(s, a!.unitId);
      const tr = activeTrigger(s, u)!;
      log(s, 'outpost', team, `${nm(s, u)} uses the ${EFFECT_NAMES[tr.kind as EffectId]} outpost.`, { unit: u.id, effect: tr.kind });
      finishActivation(s);
      s.queue.push(tr);
      break;
    }
    case 'guard': {
      const u = unitById(s, a!.unitId);
      u.guarding = true;
      log(s, 'guard', team, `${nm(s, u)} stands guard: its guard zone stops enemies until the round ends.`, { unit: u.id, role: u.role });
      finishActivation(s);
      break;
    }
    case 'dropFlag': {
      const u = unitById(s, a!.unitId);
      const to = action.to;
      u.carrying = null;
      s.flags[other(team)] = { pos: { q: to.q, r: to.r }, carrierId: null };
      log(s, 'flag-drop', team, `${nm(s, u)} puts the flag down at ${to.q},${to.r}.`, { unit: u.id, role: u.role, q: to.q, r: to.r });
      // Put down exactly on your own HQ hex, it is home.
      if (hexEq(to, hqOf(s.cells, team))) endGame(s, team, 'flag');
      if (s.active) finishActivation(s);
      break;
    }
    case 'endActivation':
      finishActivation(s);
      break;
    case 'jailbreak': {
      const u = unitById(s, a!.unitId);
      finishActivation(s);
      const freed = prisoners(s, team);
      for (const x of freed) {
        x.inPrison = false;
        delete x.respawnRound;
        if (s.config.prisonersOnBoard) continue;   // already standing there; one jailed this round stays exhausted
        // Before 6.2: onto the prison cell, and ready to act.
        x.pos = prisonSpot(s, team);
        x.facing = facingFor(s, team, x.pos);
        x.activated = false;
      }
      // Freed units that haven't acted this round can still act.
      s.activations[team].allowed = s.activations[team].used + s.units.filter(x => x.team === team && !x.inPrison && !x.activated).length;
      const ready = freed.filter(x => !x.activated), tired = freed.filter(x => x.activated);
      const names = (xs: Unit[]) => xs.map(x => nm(s, x)).join(', ');
      log(s, 'jailbreak', team, `${nm(s, u)} breaks open the prison: ${names(freed)} ${freed.length === 1 ? 'is' : 'are'} free.`
        + (ready.length ? ` ${names(ready)} can act this round.` : '') + (tired.length ? ` ${names(tired)} went to prison this round and act${tired.length === 1 ? 's' : ''} next round.` : ''),
        { unit: u.id, freed: freed.length });
      break;
    }
    case 'sprint': {
      const u = unitById(s, a!.unitId);
      army(s, team).sprintUsed = true;
      a!.movesLeft += SPRINT_MOVE;
      a!.sprinted = true;
      log(s, 'sprint', team, `${nm(s, u)} sprints: +${SPRINT_MOVE} Move.`, { unit: u.id });
      break;
    }
    case 'rally': {
      const u = unitById(s, a!.unitId);
      army(s, team).rallyUsed = true;
      u.pos = blinkSpot(s, u, action.cell)!;
      u.facing = facingFor(s, team, u.pos);
      a!.moved = true;
      a!.moveLocked = true;
      log(s, 'rally', team, `${nm(s, u)} rallies to cell ${action.cell} at ${u.pos.q},${u.pos.r}.`, { unit: u.id, cell: action.cell });
      break;
    }
    case 'pass': {
      const u = unitById(s, a!.unitId), x = unitById(s, action.toUnitId);
      army(s, team).passUsed = true;
      u.carrying = null;
      x.carrying = 'flag';
      s.flags[other(team)] = { pos: null, carrierId: x.id };
      log(s, 'pass', team, `${nm(s, u)} passes the flag to ${nm(s, x)}.`, { unit: u.id, to: x.id });
      checkArrival(s, x);
      if (s.active) finishActivation(s);
      break;
    }
    case 'reroll': case 'keepRoll': {
      const p = s.queue[0] as Extract<Trigger, { kind: 'reroll' }>;
      done();
      let hit = p.hit;
      if (action.type === 'reroll') {
        army(s, team).rerolls -= 1;
        const d = roll(s, 1, DIE);
        hit = d >= p.need;
        log(s, 'reroll', team, `${N[team]} rerolls: ${d}, needs ${p.need}+ (${hit ? 'hit' : 'miss'}). ${armyOf(s, team).rerolls} reroll(s) left.`,
          { attacker: p.attackerId, target: p.targetId, total: d, need: p.need, success: hit });
      }
      if (hit) tagOut(s, unitById(s, p.targetId));
      break;
    }
    case 'longShot': {
      const p = s.queue[0] as Extract<Trigger, { kind: 'longShot' }>;
      done();
      tagRoll(s, unitById(s, p.unitId), unitById(s, action.targetId), 'longShot');
      break;
    }
    case 'blink': {
      done();
      const u = unitById(s, action.unitId);
      u.pos = blinkSpot(s, u, action.cell)!;
      log(s, 'effect', team, `Blink: ${nm(s, u)} appears in cell ${action.cell} at ${u.pos.q},${u.pos.r}.`, { effect: 'blink', unit: u.id, cell: action.cell });
      break;
    }
    case 'skipEffect': {
      const k = s.queue[0].kind;
      done();
      log(s, 'effect-skip', team, `${N[team]} passes on ${k === 'longShot' ? 'the long shot' : k}.`, { effect: k });
      break;
    }
  }
  settle(s);
  return s;
}

export function getValidActions(state: CtfState, playerId: string): CtfAction['type'][] {
  return Array.from(new Set(listLegalActions(state, playerId).map(a => a.type)));
}
